import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {newVehicleApi} from '@dms/api/new-vehicle';

import {PricedItemBatchOutcomeUnknown} from '../types/PricedItemBatchOutcomeUnknown';
import {t} from '../utils/catalogueUi';
import {pricedItemGroupStore, type PricedItemGroup} from '../utils/pricedItemGroupStore';
import {useSendPricedItemBatch} from './useSendPricedItemBatch';

/** What the writes and the confirm of usePublishedPriceSave check on the open group of a year. */
export function useOpenPricedItemGroup() {
  const [getModelYear] = newVehicleApi.useLazyGetModelYearQuery();
  const {readOutcome} = useSendPricedItemBatch();

  /**
   * Whether the open group of the year takes a change. Read at call time, not through React state:
   * an editor outside the shell sees the group too. Only a published year groups: the status is
   * read from the server, and a group of a year that is not published any more (unpublished here or
   * by another author) is discarded, so the change takes the path of a draft year.
   */
  const isGroupOpen = async (yearId: string) => {
    if (isNil(pricedItemGroupStore.get(yearId))) {
      return false;
    }
    const year = await getModelYear({modelYearId: yearId}, false).unwrap();
    if (year.status === 'active') {
      return !isNil(pricedItemGroupStore.get(yearId));
    }
    pricedItemGroupStore.discard(yearId);
    return false;
  };

  /**
   * A group that holds a sent batch with an unknown outcome is read back first (spec 7.5): written,
   * its entries leave the group and the call resolves 'written'; not written, the group is reviewed
   * as usual; still unknown, it rejects with PricedItemBatchOutcomeUnknown and nothing is sent.
   */
  const resolveUnknownOutcome = async (yearId: string, group: PricedItemGroup) => {
    const pending = group.unknownOutcome;
    if (isNil(pending)) {
      return undefined;
    }
    return match(await readOutcome(yearId, pending.sent))
      .with('written', () => {
        showNotification.success(
          t('entity.newVehicle.catalogue.itemChange.applied', 'Saved. Pricelist is now {label}.', {
            label: pending.sent.label,
          })
        );
        pricedItemGroupStore.removeSent(yearId, pending.entries);
        return 'written' as const;
      })
      .with('notWritten', () => {
        pricedItemGroupStore.setUnknownOutcome(yearId, undefined);
        return undefined;
      })
      .with('unknown', () => {
        throw new PricedItemBatchOutcomeUnknown();
      })
      .exhaustive();
  };

  return {isGroupOpen, resolveUnknownOutcome};
}
